import { useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/auth";

/**
 * ResetPassword — étape 2 : lit ?token=xxx dans l'URL, demande un nouveau
 * mot de passe (+ confirmation) et POST vers /api/auth/reset-password.
 * Le token est single-use et expire au bout de 30 min côté serveur.
 */
export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(() => new URLSearchParams(location.search).get("token") || "", [location.search]);
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const canSubmit = token && pwd.length >= 6 && pwd === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success("Mot de passe mis à jour", { description: "Connectez-vous avec votre nouveau mot de passe." });
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <main data-testid="reset-invalid" className="flex justify-center px-6 pt-12 sm:pt-20 pb-16">
        <div className="paper p-10 max-w-md text-center space-y-4">
          <h1 className="font-heading text-2xl">Lien invalide</h1>
          <p className="text-sm text-muted-foreground">
            Le lien de réinitialisation est incomplet ou manquant. Merci de refaire une demande depuis la page de connexion.
          </p>
          <Link to="/mot-de-passe-oublie" data-testid="reset-request-again" className="btn-primary w-full inline-flex items-center justify-center">
            Refaire une demande
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="reset-page" className="flex justify-center px-6 pt-12 sm:pt-20 pb-16">
      <form onSubmit={submit} className="paper p-8 lg:p-10 w-full max-w-md space-y-5">
        <div>
          <p className="eyebrow mb-2">Espace professionnel</p>
          <h1 className="text-3xl">Nouveau mot de passe</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Choisissez un mot de passe robuste (6 caractères minimum).
          </p>
        </div>
        <div>
          <Label>Nouveau mot de passe *</Label>
          <Input
            data-testid="reset-new-password"
            type="password"
            required
            minLength={6}
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
          />
        </div>
        <div>
          <Label>Confirmez le mot de passe *</Label>
          <Input
            data-testid="reset-confirm-password"
            type="password"
            required
            minLength={6}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          {confirm && pwd !== confirm && (
            <p className="text-xs text-destructive mt-1">Les mots de passe ne correspondent pas.</p>
          )}
        </div>
        <Button data-testid="reset-submit" type="submit" className="btn-primary w-full" disabled={loading || !canSubmit}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Mettre à jour <ShieldCheck className="w-4 h-4" /></>}
        </Button>
        <p className="text-sm text-muted-foreground text-center">
          <Link to="/login" data-testid="reset-back-login" className="text-primary font-medium hover:underline inline-flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> Retour à la connexion
          </Link>
        </p>
      </form>
    </main>
  );
}
